import React, {
  memo,
  type ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ButtonSelect,
  MenuMulti,
  type MenuOption,
} from '@saviynt/design-system';

import { ADVANCED_FILTERS_METHODS } from '../../constants';
import {
  useAsyncDebounce,
  useInvertedLocalize,
  useOutsideClick,
} from '../../hooks';
import { createDevRejectionHandler } from '../../utils';
import { OUTSIDE_CLICK_SELECTORS } from '../../utils/clickOutsideUtils';

import { type SodAdvancedFiltersSharedFunc } from './SodControls';

interface FilterRefType {
  openDropdown: () => void;
  closeDropdown: () => void;
}

const CHIP_FILTER_IGNORE_SELECTORS = [
  OUTSIDE_CLICK_SELECTORS.menuMultiDropdown,
];

interface ChipFilterProps
  extends Omit<SodAdvancedFiltersSharedFunc, 'handleFilterChange'> {
  value: string;
  label: string;
  method: string;
  options: MenuOption[];
  activeOptions: MenuOption[];
  enabledFilterValue?: string | null;
  setEnabledFilterValue?: (value: string | null) => void;
  searchDebounce: number;
  loadingText: string;
  noDataText: string | undefined;
  footerLink1Text: string;
  footerLink2Text: string;
  filterSearchError?: string;
  onFooterLink1Click: SodAdvancedFiltersSharedFunc['handleFilterChange'];
  isDisabled?: boolean;
  minOptionsForSearch?: number;
  /** Key of the currently open filter pill — used to close this pill when another opens */
  activeFilterKey?: string | null;
  /** Called with this filter's value when its dropdown opens */
  onOpen?: (value: string) => void;
}

function ChipFilter({
  value,
  label,
  method = ADVANCED_FILTERS_METHODS.API,
  options = [],
  activeOptions = [],
  handleFilterSearch,
  searchDebounce,
  enabledFilterValue,
  setEnabledFilterValue,
  noDataText,
  loadingText,
  footerLink1Text,
  footerLink2Text,
  filterSearchError = '',
  handleOptionsClick,
  onFooterLink1Click,
  isDisabled,
  minOptionsForSearch,
  activeFilterKey,
  onOpen,
}: ChipFilterProps): ReactElement {
  const l = useInvertedLocalize();
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState<MenuOption[]>(options);
  const [isError, setIsError] = useState(false);

  const filterRef = useRef<FilterRefType | null>(null);
  const menuContainerRef = useRef<HTMLDivElement | null>(null);
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);

  // When footerLink1 text is empty, the DS still renders its button in the portal.
  // Find it by its empty text content and remove it from the tab order.
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (isMenuExpanded && footerLink1Text.trim().length === 0) {
      timeoutId = setTimeout(() => {
        const dropdowns = document.querySelectorAll<HTMLElement>(
          '[class*="MenuMulti-dropdown"]'
        );

        dropdowns.forEach((dropdown) => {
          dropdown
            .querySelectorAll<HTMLElement>('button[class*="ButtonLink"]')
            .forEach((btn) => {
              if (!btn.textContent?.trim()) {
                btn.tabIndex = -1;
                btn.setAttribute('aria-hidden', 'true');
              }
            });
        });
      }, 50);
    }

    return () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [isMenuExpanded, footerLink1Text]);

  // Close this pill when another filter pill opens (mouse-click path)
  useEffect(() => {
    if (!activeFilterKey || activeFilterKey === value || !isMenuExpanded) {
      return;
    }

    setIsMenuExpanded(false);
    filterRef.current?.closeDropdown();
  }, [activeFilterKey, value, isMenuExpanded]);

  // Close this pill when keyboard focus moves outside its container or portal dropdown.
  // This handles Tab-navigation between filter pills, where useOutsideClick (mousedown-based)
  // and the activeFilterKey coordination (needs the new pill to open first) are not sufficient.
  useEffect(() => {
    if (!isMenuExpanded) {
      return undefined;
    }

    const handleFocusin = (e: FocusEvent) => {
      const target = e.target as Element | null;

      if (!target) {
        return;
      }

      // Focus is inside this filter's trigger/container — keep open
      if (menuContainerRef.current?.contains(target)) {
        return;
      }

      // Focus is inside a MenuMulti dropdown portal — keep open
      // (covers this pill's own portal and prevents premature close while tabbing through options)
      if (target.closest('[class*="MenuMulti-dropdown"]')) {
        return;
      }

      // Focus moved outside this filter — close it
      setIsMenuExpanded(false);
      filterRef.current?.closeDropdown();
    };

    document.addEventListener('focusin', handleFocusin);

    return () => document.removeEventListener('focusin', handleFocusin);
  }, [isMenuExpanded]);

  const PROGRAMMATIC_OPEN_DELAY_MS = 200;

  // Programmatic open when parent requests (e.g. from breadcrumb)
  useEffect(() => {
    const shouldOpen = enabledFilterValue && value === enabledFilterValue;

    if (!shouldOpen) {
      return undefined;
    }

    // Delay opening slightly so the container transition is smooth.
    const timeoutId = window.setTimeout(() => {
      filterRef.current?.openDropdown();
      setEnabledFilterValue?.(null);
    }, PROGRAMMATIC_OPEN_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [value, enabledFilterValue, setEnabledFilterValue]);

  useOutsideClick({
    isEnabled: isMenuExpanded,
    containerRef: menuContainerRef,
    ignoreSelectors: CHIP_FILTER_IGNORE_SELECTORS,
    onOutsideClick: () => {
      setIsMenuExpanded(false);
      filterRef.current?.closeDropdown();
    },
  });

  useEffect(() => {
    if (options.length) {
      setTimeout(
        () =>
          setFilterOptions(
            options.map((f) => ({
              ...f,
              isChecked: activeOptions.some((_f) => _f.value === f.value),
            }))
          ),
        0
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should be one time update on activeOptions
  }, [options]);

  useEffect(() => {
    if (filterOptions.length === 0) {
      setFilterOptions((prev) => [...prev, ...activeOptions]);

      return;
    }

    setFilterOptions((prev) =>
      prev.map((prevOption) => ({
        ...prevOption,
        isChecked: activeOptions.some(
          (option) => option.value === prevOption.value
        ),
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- want to exclude filter.length
  }, [activeOptions]);

  const handleDebouncedFilterSearch = useAsyncDebounce(
    handleFilterSearch,
    searchDebounce
  );

  const handleLocalFilterSearch = async <T extends string>(
    key: T,
    searchString: T
  ) => {
    if (method === ADVANCED_FILTERS_METHODS.SEARCH && !searchString) {
      setFilterOptions((prev) => prev.filter((option) => option.isChecked));
      setIsLoading(false);

      return;
    }

    setIsError(false);
    setFilterOptions((prev) => prev.filter((option) => option.isChecked));

    const responseOptions = await handleDebouncedFilterSearch(
      key,
      searchString
    );

    if (responseOptions === undefined) {
      setIsError(true);

      return;
    }

    if (responseOptions === false) {
      setFilterOptions([]);

      return;
    }

    const newOptions = responseOptions as MenuOption[];

    setFilterOptions((prev) => {
      const prevCheckedOptions = prev.filter((option) => option.isChecked);
      const optionsToExclude = new Set(
        prevCheckedOptions.map((option) => option.value)
      );
      const newVerifiedOptions = newOptions.filter(
        (option) => !optionsToExclude.has(option.value)
      );

      return [...prevCheckedOptions, ...newVerifiedOptions];
    });

    setIsLoading(false);
  };

  const optionsCountForSearch =
    method === ADVANCED_FILTERS_METHODS.FIXED
      ? Number(filterOptions.length) + 1
      : 0;

  const minOptionsForSearchValue =
    typeof minOptionsForSearch === 'number'
      ? minOptionsForSearch
      : optionsCountForSearch;

  const getNoDataText = () => {
    if (isError) {
      return filterSearchError ? l(filterSearchError) : '';
    }

    if (isLoading) {
      return l(loadingText);
    }

    if (method === ADVANCED_FILTERS_METHODS.SEARCH && !searchText) {
      return noDataText ? l(noDataText) : undefined;
    }

    return undefined;
  };

  return (
    <div ref={menuContainerRef}>
      <MenuMulti
        ref={filterRef as unknown as React.RefObject<HTMLDivElement>}
        key={value}
        options={filterOptions}
        appendLabelsToButton
        label={l(label)}
        onChange={(selectedOptions) => {
          handleOptionsClick(value, method, selectedOptions ?? []);
        }}
        setIsExpanded={(isOpen) => {
          setIsMenuExpanded(isOpen);

          if (isOpen) {
            onOpen?.(value);
          } else {
            setIsError(false);
          }
        }}
        trigger={<ButtonSelect kind='pill' label='' isDisabled={isDisabled} />}
        minOptionsForFooter={0}
        noDataText={
          method === ADVANCED_FILTERS_METHODS.INACTIVE
            ? 'WIP until API is integrated'
            : getNoDataText()
        }
        showFooterLink1={footerLink1Text.trim().length > 0}
        footerLink1Text={l(footerLink1Text)}
        footerLink2Text={l(footerLink2Text)}
        onFooterLink1Click={() => {
          onFooterLink1Click({
            value,
            isChecked: true,
            method,
          });
        }}
        onFooterLink2Click={() => {
          handleOptionsClick(value, method, []);
        }}
        isFilteringDisabled
        minOptionsForSearch={minOptionsForSearchValue}
        onSearchTermChange={(searchString) => {
          setIsLoading(true);
          setSearchText(searchString);
          handleLocalFilterSearch(value, searchString).catch((err: unknown) => {
            createDevRejectionHandler('ChipFilter: handleLocalFilterSearch')(
              err
            );
          });
        }}
        showOptionLabelOnHover
      />
    </div>
  );
}

ChipFilter.defaultProps = {
  filterSearchError: '',
  enabledFilterValue: null,
  setEnabledFilterValue: () => {},
  isDisabled: false,
  minOptionsForSearch: undefined,
  activeFilterKey: null,
  onOpen: undefined,
};

export default memo(ChipFilter);
